import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/router'
import { AButton, ASvg } from 'ui'
import SvgCloseIcon from 'ui/svg/icons/stroke/close.svg'

export type CEntryModalBlock = {
  __typename?: 'entryModal_block_BlockType'
  headline?: string
  wysiwyg?: string
  buttonLabel?: string
  buttonUrl?: string
  delayShow?: boolean
  delayTime?: string
  closeFrequency?: 'session' | 'page' | 'none' | '1Day' | '7Days' | '30Days'
}

type Props = {
  field?: Array<CEntryModalBlock>
}

const SESSION_KEY = 'discovericl-entry-modal-dismissed'
const PAGE_KEY = 'discovericl-entry-modal-page-dismissed'
const LOCAL_KEY = 'discovericl-entry-modal-dismissed-until'
const DEFAULT_DELAY_MS = 5000

const isExternalUrl = (href?: string) => /^https?:\/\//i.test(href ?? '')

const hasRenderableHtml = (html?: string) => {
  if (!html) return false

  return Boolean(
    html
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .trim()
  )
}

const getDelayMs = (delayShow?: boolean, delayTime?: string) => {
  const parsedDelay = Number(delayTime?.trim())

  if (Number.isFinite(parsedDelay) && parsedDelay > 0) {
    return parsedDelay
  }

  if (delayShow) {
    return DEFAULT_DELAY_MS
  }

  return 0
}

const getDismissDurationMs = (
  closeFrequency?: 'session' | 'page' | 'none' | '1Day' | '7Days' | '30Days'
) => {
  switch (closeFrequency) {
    case '1Day':
      return 24 * 60 * 60 * 1000
    case '7Days':
      return 7 * 24 * 60 * 60 * 1000
    case '30Days':
      return 30 * 24 * 60 * 60 * 1000
    default:
      return null
  }
}

const hasLocalDismissal = () => {
  if (typeof window === 'undefined') return false

  const storedValue = window.localStorage.getItem(LOCAL_KEY)

  if (!storedValue) return false

  try {
    const parsedValue = JSON.parse(storedValue) as { expiresAt?: number }

    if (
      parsedValue?.expiresAt &&
      Number.isFinite(parsedValue.expiresAt) &&
      parsedValue.expiresAt > Date.now()
    ) {
      return true
    }

    window.localStorage.removeItem(LOCAL_KEY)
    return false
  } catch {
    window.localStorage.removeItem(LOCAL_KEY)
    return false
  }
}

const CEntryModal = ({ field }: Props) => {
  const router = useRouter()
  const block = field?.[0]

  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)

  const headline = block?.headline?.trim()
  const wysiwyg = block?.wysiwyg
  const buttonLabel = block?.buttonLabel?.trim()
  const buttonUrl = block?.buttonUrl?.trim()
  const delayShow = Boolean(block?.delayShow)
  const delayTime = block?.delayTime?.trim()
  const closeFrequency = block?.closeFrequency ?? 'session'

  const hasContent = Boolean(headline || hasRenderableHtml(wysiwyg))

  const handleClose = () => {
    setOpen(false)

    if (typeof window === 'undefined') return

    if (closeFrequency === 'none') return

    if (closeFrequency === 'page') {
      window.sessionStorage.setItem(PAGE_KEY, '1')
      return
    }

    if (closeFrequency === 'session') {
      window.sessionStorage.setItem(SESSION_KEY, '1')
      return
    }

    const durationMs = getDismissDurationMs(closeFrequency)

    if (!durationMs) return

    window.localStorage.setItem(
      LOCAL_KEY,
      JSON.stringify({ expiresAt: Date.now() + durationMs })
    )
  }

  const handleButtonClick = () => {
    handleClose()

    if (!buttonUrl) return

    if (isExternalUrl(buttonUrl)) {
      window.location.assign(buttonUrl)
      return
    }

    router.push(buttonUrl)
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || !hasContent || typeof window === 'undefined') return

    const pageDismissed = window.sessionStorage.getItem(PAGE_KEY)
    const sessionDismissed = window.sessionStorage.getItem(SESSION_KEY)
    const localDismissed = hasLocalDismissal()

    if (closeFrequency === 'page' && pageDismissed) return
    if (closeFrequency === 'session' && sessionDismissed) return
    if (
      (closeFrequency === '1Day' ||
        closeFrequency === '7Days' ||
        closeFrequency === '30Days') &&
      localDismissed
    ) {
      return
    }

    const delayMs = getDelayMs(delayShow, delayTime)

    if (delayMs > 0) {
      const timeoutId = window.setTimeout(() => {
        setOpen(true)
      }, delayMs)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }

    setOpen(true)
  }, [mounted, hasContent, delayShow, delayTime, closeFrequency])

  useEffect(() => {
    if (!open || typeof window === 'undefined') return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = originalOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (!mounted || !hasContent || !open) {
    return null
  }

  return createPortal(
    <div className="fixed inset-0 z-[80]">
      <button
        type="button"
        aria-label="Close modal overlay"
        className="absolute inset-0 h-full w-full bg-black/60"
        onClick={handleClose}
      />

      <div className="absolute inset-0 flex items-center justify-center p-4 md:p-6">
        <div className="relative w-full max-w-[760px] max-h-[calc(100dvh-2rem)] md:max-h-[calc(100dvh-3rem)] overflow-y-auto bg-neutral-white px-6 py-10 md:px-12 md:py-12 shadow-xl">
          <button
            type="button"
            aria-label="Close modal"
            className="absolute right-3 top-3 md:right-4 md:top-4"
            onClick={handleClose}
          >
            <ASvg className="stroke-current w-8 h-8" svg={SvgCloseIcon} />
          </button>

          <div className="mx-auto flex w-full max-w-[640px] flex-col gap-4 text-center">
            {headline && (
              <h2 className="text-3xl font-body text-center">
                {headline}
              </h2>
            )}

            {hasRenderableHtml(wysiwyg) && (
              <div
                className="font-md text-center [&_a]:underline [&_a]:text-primary-dark-teal [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: wysiwyg ?? '' }}
              />
            )}

            {buttonLabel && buttonUrl && (
              <div className="mt-2 text-center">
                <AButton
                  className="w-full"
                  size="md"
                  type="button"
                  onClick={handleButtonClick}
                >
                  <>{buttonLabel}</>
                </AButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export { CEntryModal }

export const CEntryModalFragments = {
  field: `
    fragment entryModal on entryModal_MatrixField {
      ... on entryModal_block_BlockType {
        __typename
        headline
        wysiwyg
        buttonLabel
        buttonUrl
        delayShow
        delayTime
        closeFrequency
      }
    }
  `,
}